import { OutputData } from '@editorjs/editorjs'
import React, { memo, useMemo } from 'react'

import { EditorJsBenefitParagraphHtml } from '@/_components/universal/editorjs-renderer/components/editorjs-benefit-paragraph-html'
import { sanitizeAndProcessHtml } from '@/_lib/dom-purify.utils'

// Utility functions moved outside components to prevent recreation
const getItemContent = (item: any) => {
	return item?.content?.trim()?.startsWith('+ ')
		? sanitizeAndProcessHtml(item?.content?.trim()?.substring(2), false)
		: sanitizeAndProcessHtml(item?.content, false)
}

const isExtraItem = (item: any) => {
	return item?.content?.trim()?.startsWith('+ ')
}

// Create a stable item wrapper to prevent unnecessary re-renders
const createStableItem = (item: any, index: number) => ({
	id: item.id || index,
	content: item.content,
	items: item.items,
	// Create a stable reference by stringifying the content
	stableKey: JSON.stringify({ content: item.content, items: item.items })
})

export const AListItem = memo(
	({ item, isExtraItem: isExtra }: { item: any; isExtraItem?: boolean }) => {
		const sanitizedContent = useMemo(() => {
			return getItemContent(item) || ''
		}, [item])

		const subItems = useMemo(() => {
			if (!item.items || item.items.length === 0) return null

			return item.items.map((subItem: any, subIndex: number) => ({
				id: `${item.id}-list-${subIndex}`,
				content: getItemContent(subItem) || ''
			}))
		}, [item.items, item.id])

		return (
			<li className={isExtra ? 'extra' : ''}>
				<span dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
				{subItems && (
					<ul className='list-disc !mt-0'>
						{subItems.map((subItem: { id: string; content: string }) => (
							<li key={subItem.id}>
								<span dangerouslySetInnerHTML={{ __html: subItem.content }} />
							</li>
						))}
					</ul>
				)}
			</li>
		)
	}
)

AListItem.displayName = 'AListItem'

export const ListItems = memo(
	({
		items,
		blockId,
		itemIndex,
		groupIndex,
		isExtra
	}: {
		items: any[]
		blockId: string
		itemIndex: number
		groupIndex: number
		isExtra: boolean
	}) => {
		const stableItems = useMemo(
			() => items.map((item, index) => createStableItem(item, index)),
			[items]
		)

		return (
			<ul className={`list-disc ${isExtra ? 'extra' : ''}`}>
				{stableItems.map((item, listIndex) => (
					<AListItem
						key={`${blockId}-list-${itemIndex}-${groupIndex}-${
							isExtra ? 'extra' : 'standard'
						}-${listIndex}`}
						item={item}
						isExtraItem={isExtra}
					/>
				))}
			</ul>
		)
	}
)

ListItems.displayName = 'ListItems'

export const BenefitBlock = memo(
	({
		block,
		benefitId,
		itemIndex
	}: {
		block: any
		benefitId: string | number
		itemIndex: number
	}) => {
		if (block.type === 'paragraph') {
			return (
				<EditorJsBenefitParagraphHtml
					key={`${benefitId}-paragraph-${itemIndex}`}
					html={block.data.text ?? ''}
					className='!mt-0'
				/>
			)
		}

		if (block.type === 'list' || block.type === 'nestedList') {
			const listItemGroups: { type: 'standard' | 'extra'; items: any[] }[] = []

			for (const item of block.data.items) {
				if (isExtraItem(item)) {
					if (listItemGroups[listItemGroups.length - 1]?.type === 'extra') {
						listItemGroups[listItemGroups.length - 1].items.push(item)
					} else {
						listItemGroups.push({ type: 'extra', items: [item] })
					}
				} else {
					if (listItemGroups[listItemGroups.length - 1]?.type === 'standard') {
						listItemGroups[listItemGroups.length - 1].items.push(item)
					} else {
						listItemGroups.push({ type: 'standard', items: [item] })
					}
				}
			}

			return (
				<React.Fragment key={`${benefitId}-list-${itemIndex}`}>
					{listItemGroups.map((group, groupIndex) => (
						<ListItems
							key={`${benefitId}-list-${itemIndex}-${groupIndex}-${group.type}`}
							items={group.items}
							blockId={block.id}
							itemIndex={itemIndex}
							groupIndex={groupIndex}
							isExtra={group.type === 'extra'}
						/>
					))}
					{/* {standardItems.length > 0 && (
						<ListItems
							items={standardItems}
							blockId={block.id}
							itemIndex={itemIndex}
							isExtra={false}
						/>
					)}

					{extraItems.length > 0 && (
						<ListItems
							items={extraItems}
							blockId={block.id}
							itemIndex={itemIndex}
							isExtra={true}
						/>
					)} */}
				</React.Fragment>
			)
		}

		return null
	}
)

BenefitBlock.displayName = 'BenefitBlock'

const hasDescriptionBlocks = (data: OutputData | null | undefined): data is OutputData =>
	!!data &&
	typeof data === 'object' &&
	Array.isArray((data as OutputData).blocks) &&
	(data as OutputData).blocks.length > 0

export const BenefitBlocks = memo(({ data, id }: { data: OutputData; id: string | number }) => {
	if (!hasDescriptionBlocks(data)) return null

	const blocks = useMemo(
		() =>
			data.blocks.map((block, itemIndex) => ({
				block,
				itemIndex
			})),
		[data.blocks]
	)

	return (
		<div key={`${id}-blocks`}>
			{blocks.map(({ block, itemIndex }) => (
				<BenefitBlock
					key={`${id}-block-${itemIndex}`}
					block={block}
					benefitId={id}
					itemIndex={itemIndex}
				/>
			))}
		</div>
	)
})

BenefitBlocks.displayName = 'BenefitBlocks'
